<script setup>
import { computed } from 'vue';
import Icon from 'next/icon/Icon.vue';
import { useI18n } from 'vue-i18n';
import { useMessageContext } from './provider.js';
import { hasOneDayPassed } from 'shared/helpers/timeHelper';
import { ORIENTATION, MESSAGE_STATUS } from './constants';
import { useStore } from 'dashboard/composables/store';
import { emitter } from 'shared/helpers/mitt';

const props = defineProps({
  error: { type: String, required: true },
});

const emit = defineEmits(['retry']);
const store = useStore();

const { orientation, status, createdAt, content, attachments, conversationId, id } =
  useMessageContext();

const { t } = useI18n();

const isWindowClosedError = computed(() => {
  return props.error && props.error.includes('131047');
});

const canRetry = computed(() => {
  if (isWindowClosedError.value) return false;
  const hasContent = content.value !== null;
  const hasAttachments = attachments.value && attachments.value.length > 0;
  return !hasOneDayPassed(createdAt.value) && (hasContent || hasAttachments);
});

const handleSendTemplate = () => {
  store.dispatch('deleteMessage', { conversationId: conversationId.value, messageId: id.value });
  emitter.emit('SHOW_WHATSAPP_TEMPLATES');
};
</script>

<template>
  <div class="text-xs text-n-ruby-11 flex items-center gap-1.5">
    <span v-if="!isWindowClosedError">{{ t('CHAT_LIST.FAILED_TO_SEND') }}</span>
    <div v-if="!isWindowClosedError" class="relative group">
      <div
        class="bg-n-alpha-2 rounded-md size-5 grid place-content-center cursor-pointer"
      >
        <Icon
          icon="i-lucide-alert-triangle"
          class="text-n-ruby-11 size-[14px]"
        />
      </div>
      <div
        class="absolute bg-n-alpha-3 px-4 py-3 border rounded-xl border-n-strong text-n-slate-12 bottom-6 w-52 text-xs backdrop-blur-[100px] shadow-[0px_0px_24px_0px_rgba(0,0,0,0.12)] opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all break-all"
        :class="{
          'ltr:left-0 rtl:right-0': orientation === ORIENTATION.LEFT,
          'ltr:right-0 rtl:left-0': orientation === ORIENTATION.RIGHT,
        }"
      >
        {{ error }}
      </div>
    </div>
    <button
      v-if="canRetry"
      type="button"
      :disabled="status !== MESSAGE_STATUS.FAILED"
      class="bg-n-alpha-2 rounded-md size-5 grid place-content-center cursor-pointer"
      @click="emit('retry')"
    >
      <Icon icon="i-lucide-refresh-ccw" class="text-n-ruby-11 size-[14px]" />
    </button>
    <button
      type="button"
      class="bg-n-ruby-9 hover:bg-n-ruby-10 text-white rounded-md px-3 py-1 font-medium transition-colors cursor-pointer"
      @click="handleSendTemplate"
    >
      Send Template
    </button>
  </div>
</template>
